import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { videos, hotKeywords } from "@/mocks/videos";
import VideoCard from "@/components/feature/VideoCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import usePageMeta from "@/hooks/usePageMeta";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q")?.trim() ?? "";
  const [keyword, setKeyword] = useState(query);

  const results = useMemo(() => {
    if (!query) return [];
    const kw = query.toLowerCase();
    return videos.filter(
      (v) => v.title.toLowerCase().includes(kw) || v.summary.toLowerCase().includes(kw),
    );
  }, [query]);

  usePageMeta({
    title: query ? `搜索「${query}」 - 吃瓜视频` : "搜索 - 吃瓜视频",
    description: query
      ? `吃瓜视频中与「${query}」相关的视频搜索结果，共 ${results.length} 条。`
      : "在吃瓜视频搜索明星动态、社会热点与趣味短片。",
    keywords: query ? `${query},吃瓜视频,视频搜索` : "吃瓜视频,视频搜索,娱乐视频",
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    setParams({ q });
  };

  return (
    <div className="w-full px-4 py-6 md:px-8 md:py-8">
      <Breadcrumb items={[{ label: "首页", href: "/" }, { label: "搜索结果" }]} />

      <h1 className="mt-5 font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-3xl">
        {query ? `搜索：${query}` : "全站搜索"}
      </h1>

      <form onSubmit={submit} className="mt-5 flex max-w-2xl flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <span className="pointer-events-none absolute left-4 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-base" />
          </span>
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            type="text"
            name="q"
            placeholder="搜索视频标题或简介"
            className="w-full rounded-full border border-background-200 bg-background-50 py-3 pl-11 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
        <button
          type="submit"
          className="rounded-full bg-primary-500 px-7 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
        >
          重新搜索
        </button>
      </form>

      {query && (
        <p className="mt-5 text-sm text-foreground-600">
          为你找到 <span className="font-semibold text-primary-600">{results.length}</span> 条与「
          <span className="font-medium text-foreground-900">{query}</span>」相关的视频
        </p>
      )}

      {!query ? (
        <div className="mt-8 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-3xl text-primary-500">
            <i className="ri-search-eye-line" />
          </span>
          <h2 className="mt-4 font-heading text-base font-bold text-foreground-950">输入关键词开始搜索</h2>
          <p className="mt-2 text-sm text-foreground-600">支持搜索视频标题与简介内容，例如「歌手」「暴雨」。</p>
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            {hotKeywords.slice(0, 6).map((kw) => (
              <Link
                key={kw}
                to={`/search?q=${encodeURIComponent(kw)}`}
                className="rounded-full border border-background-200 bg-background-50 px-3 py-1 text-xs text-foreground-700 transition-colors hover:border-accent-300 hover:text-accent-600"
              >
                {kw}
              </Link>
            ))}
          </div>
        </div>
      ) : results.length === 0 ? (
        <div className="mt-8 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-3xl text-secondary-600">
            <i className="ri-emotion-sad-line" />
          </span>
          <h2 className="mt-4 font-heading text-base font-bold text-foreground-950">没有找到相关视频</h2>
          <p className="mt-2 text-sm text-foreground-600">换个关键词试试，或看看下面的热门搜索。</p>
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            {hotKeywords.map((kw) => (
              <Link
                key={kw}
                to={`/search?q=${encodeURIComponent(kw)}`}
                className="rounded-full border border-background-200 bg-background-50 px-3 py-1 text-xs text-foreground-700 transition-colors hover:border-accent-300 hover:text-accent-600"
              >
                {kw}
              </Link>
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-5">
          {results.map((video) => (
            <VideoCard key={video.id} video={video} />
          ))}
        </div>
      )}
    </div>
  );
}